import { defineStore } from 'pinia';
import { markRaw, nextTick, ref, shallowRef } from 'vue';
import vtkPLYReader from '@kitware/vtk.js/IO/Geometry/PLYReader';
import vtkSTLReader from '@kitware/vtk.js/IO/Geometry/STLReader';
import type vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { adjacency, connectedBrush, pointNormals } from './meshTopology';
import { identity, type Point3 } from './rigid';
import { CATEGORICAL_COLORS } from '@/src/config';
import type { DentalAnnotations, DentalAsset, DentalInstance } from './types';

type MeshEdit = { ids: number[]; before: number[]; after: number[] };
export const useDentalMeshStore = defineStore('dental-mesh', () => {
  const mesh = shallowRef<vtkPolyData>();
  const labels = shallowRef(new Uint16Array());
  const matrix = ref(identity());
  const sourceName = ref('');
  const sourceAssetId = ref(''); const sourceVersion = ref(1);
  const frameId = ref('');
  const changed = ref(0);
  const dirty = ref(false); const labelsDirty = ref(false); const editable = ref(true);
  const selectedValue = ref(1);
  const radiusMm = ref(2);
  const maxNormalAngle = ref(90); const notice = ref('');
  const mode = ref<'navigate' | 'brush' | 'eraser' | 'select' | 'landmark' | 'source-pick'>('navigate');
  const renderInVolume = ref(false);
  const volumeVisible = ref(true);
  const labelAssetId = ref<string | null>(null); const instances = ref<DentalInstance[]>([]); const landmarkName = ref('Landmark 1');
  const landmarks = ref<{ id: string; name: string; frameId: string; xyz: Point3; pointId: number; triangleId: number; meshVersion: number }[]>([]);
  const undoCount = ref(0); const redoCount = ref(0);
  const undoStack: MeshEdit[] = []; const redoStack: MeshEdit[] = [];
  let graph: number[][] = [];
  let normals = new Float32Array(); let stroke: Map<number, number> | undefined;
  function historyCounts() { undoCount.value = undoStack.length; redoCount.value = redoStack.length; }
  function clearHistory() { undoStack.length = 0; redoStack.length = 0; stroke = undefined; historyCounts(); }
  function edited(labelsChanged = true) { changed.value++; dirty.value = true; if (labelsChanged) labelsDirty.value = true; }
  function instance(value: number) { return instances.value.find(row => row.labelValue === value); }
  function addInstance(value: number, name: string, fdi: number | null = null) {
    if (!Number.isInteger(value) || value < 1 || value > 65535 || instance(value) || !name.trim()) throw new Error('Choose an unused uint16 label value and a name.');
    instances.value.push({ id: crypto.randomUUID(), labelValue: value, name: name.trim(), fdi,
      color: [...CATEGORICAL_COLORS[instances.value.length % CATEGORICAL_COLORS.length], 255] as DentalInstance['color'], visible: true, locked: false });
    selectedValue.value = value; edited(false);
  }
  function updateInstance(row: DentalInstance, patch: Partial<DentalInstance>) { Object.assign(row, patch); edited(false); }

  async function open(file: File, units: string, asset?: DentalAsset) {
    if (units !== 'mm') throw new Error('Declare the native mesh units explicitly as mm.');
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!['ply', 'stl'].includes(extension ?? '')) throw new Error('This mesh workspace accepts PLY and STL.');
    const reader = extension === 'ply' ? vtkPLYReader.newInstance({ duplicatePointsForFaceTexture: false }) : vtkSTLReader.newInstance();
    reader.parseAsArrayBuffer(await file.arrayBuffer());
    const poly = reader.getOutputData() as vtkPolyData;
    const count = poly.getNumberOfPoints();
    if (!count || !poly.getNumberOfPolys()) throw new Error('A nonempty polygon mesh is required.');
    if (asset && (count !== asset.pointCount || poly.getNumberOfPolys() !== asset.cellCount)) throw new Error('Reader indices differ from the canonical asset counts. Import was refused.');
    const points = poly.getPoints().getData();
    for (let i = 0; i < points.length; i++) if (!Number.isFinite(points[i])) throw new Error('Mesh coordinates must be finite.');
    graph = adjacency(poly.getPolys().getData(), count);
    normals = pointNormals(points, poly.getPolys().getData());
    const previous = mesh.value;
    mesh.value = markRaw(poly); labels.value = new Uint16Array(count);
    sourceName.value = file.name; sourceAssetId.value = asset?.id || ''; sourceVersion.value = asset?.version || 1;
    frameId.value = asset?.frame.id || `mesh-native:${crypto.randomUUID()}`;
    matrix.value = identity(); landmarks.value = []; instances.value = []; labelAssetId.value = null; changed.value++;
    dirty.value = false; labelsDirty.value = false; clearHistory();
    await nextTick(); previous?.delete(); reader.delete();
  }

  async function importLabels(file: File) {
    if (!mesh.value) throw new Error('Open the canonical mesh before its labels.');
    const bytes = await file.arrayBuffer();
    const count = mesh.value.getNumberOfPoints();
    if (bytes.byteLength !== count * 2) throw new Error(`Point labels require exactly ${count * 2} bytes.`);
    const input = new DataView(bytes);
    labels.value = Uint16Array.from({ length: count }, (_, i) => input.getUint16(i * 2, true));
    changed.value++; clearHistory();
  }

  function loadAnnotations(annotations: DentalAnnotations) {
    if (!annotations.meshLabelsAssetId && mesh.value) labels.value = new Uint16Array(mesh.value.getNumberOfPoints());
    instances.value = JSON.parse(JSON.stringify(annotations.instances));
    if (!instances.value.length) for (const value of [...new Set(labels.value)].filter(Boolean).sort((a,b) => a-b)) addInstance(value, `Label ${value}`);
    landmarks.value = annotations.landmarks.map(p => ({ id: p.id, name: p.name, frameId: p.frameId, xyz: [...p.position],
      pointId: p.pointId!, triangleId: p.triangleId!, meshVersion: p.meshVersion! }));
    labelAssetId.value = annotations.meshLabelsAssetId; selectedValue.value = instances.value[0]?.labelValue || 1;
    mode.value = 'navigate'; clearHistory(); changed.value++; dirty.value = false; labelsDirty.value = false;
  }

  function beginStroke(pointId: number) { stroke = new Map(); notice.value = ''; paintStep(pointId); }
  function paintStep(pointId: number) {
    if (!mesh.value || !editable.value || !stroke || !['brush','eraser'].includes(mode.value)) return;
    if (!Number.isInteger(selectedValue.value) || selectedValue.value < 1 || selectedValue.value > 65535) { notice.value = 'Choose a positive uint16 label value.'; return; }
    if (!instance(selectedValue.value)) {
      if (sourceAssetId.value) { notice.value = 'Create an instance before painting.'; return; }
      addInstance(selectedValue.value, `Label ${selectedValue.value}`);
    }
    if (instance(selectedValue.value)?.locked) { notice.value = 'Unlock the selected instance before editing.'; return; }
    if (!Number.isFinite(maxNormalAngle.value) || maxNormalAngle.value < 0 || maxNormalAngle.value > 180) { notice.value = 'Normal angle must be in 0..180 degrees.'; return; }
    const value = mode.value === 'eraser' ? 0 : selectedValue.value;
    let ids: number[];
    try { ids = connectedBrush(mesh.value.getPoints().getData(), graph, pointId, radiusMm.value, { normals, maxAngleDegrees: maxNormalAngle.value }); }
    catch (e) { notice.value = (e as Error).message; return; }
    ids = ids.filter(id => labels.value[id] !== value && !instance(labels.value[id])?.locked && (mode.value !== 'eraser' || labels.value[id] === selectedValue.value));
    if (ids.length > 200000 || stroke.size + ids.filter(id => !stroke!.has(id)).length > 200000) { notice.value = 'Stroke exceeds the 200,000 vertex limit. Reduce the brush radius.'; return; }
    for (const id of ids) { if (!stroke.has(id)) stroke.set(id, labels.value[id]); labels.value[id] = value; }
    if (ids.length) edited();
  }
  function endStroke() {
    if (stroke?.size) {
      const ids = [...stroke.keys()].filter(id => stroke!.get(id) !== labels.value[id]);
      if (ids.length) {
        undoStack.push({ ids, before: ids.map(id => stroke!.get(id)!), after: ids.map(id => labels.value[id]) }); redoStack.length = 0;
        while (undoStack.length > 60 || undoStack.reduce((sum,e) => sum+e.ids.length,0) > 250000) undoStack.shift();
      }
    }
    stroke = undefined; historyCounts();
  }
  function paint(pointId: number) { beginStroke(pointId); endStroke(); }
  function apply(edit: MeshEdit, values: number[]) {
    if (!editable.value || [...edit.before,...edit.after].some(value => instance(value)?.locked)) { notice.value = 'Unlock affected instances before undo/redo.'; return false; }
    edit.ids.forEach((id,i) => { labels.value[id] = values[i]; }); edited(); return true;
  }

  function undo() { const edit = undoStack.at(-1); if (edit && apply(edit, edit.before)) { undoStack.pop(); redoStack.push(edit); historyCounts(); } }
  function redo() { const edit = redoStack.at(-1); if (edit && apply(edit, edit.after)) { redoStack.pop(); undoStack.push(edit); historyCounts(); } }
  function addLandmark(pointId: number, triangleId: number) {
    if (!mesh.value || !editable.value) return;
    const points = mesh.value.getPoints().getData();
    landmarks.value.push({ id: crypto.randomUUID(), name: landmarkName.value || 'Landmark', frameId: frameId.value,
      xyz: [points[pointId * 3], points[pointId * 3 + 1], points[pointId * 3 + 2]], pointId, triangleId, meshVersion: sourceVersion.value });
    landmarkName.value = `Landmark ${landmarks.value.length + 1}`; edited(false);
  }
  function removeLandmark(id: string) { landmarks.value = landmarks.value.filter(p => p.id !== id); edited(false); }
  function labelBytes() {
    const data = new Uint8Array(labels.value.length * 2); const view = new DataView(data.buffer);
    labels.value.forEach((value, i) => view.setUint16(i * 2, value, true));
    return data;
  }
  function annotations(): DentalAnnotations { return { labelmapAssetId: null, meshLabelsAssetId: labelAssetId.value, registration: null,
    instances: JSON.parse(JSON.stringify(instances.value)), landmarks: landmarks.value.map(({ xyz,...p }) => ({ ...p, position: [...xyz] })) }; }
  function markSaved(assetId: string | null) { labelAssetId.value = assetId; dirty.value = false; labelsDirty.value = false; }
  return { mesh, labels, matrix, sourceName, sourceAssetId, sourceVersion, frameId, changed, dirty, labelsDirty, editable, selectedValue,
    radiusMm, maxNormalAngle, notice, mode, renderInVolume, volumeVisible, landmarks, landmarkName, instances, labelAssetId, undoCount, redoCount,
    open, importLabels, loadAnnotations, addInstance, updateInstance, instance, beginStroke, paintStep, endStroke, paint, undo, redo,
    addLandmark, removeLandmark, labelBytes, annotations, markSaved };
});
